import classNames from "classnames";
import React, { useRef, useState } from "react";
import style from "./timer-controller.module.css";
import { ProgressLoader } from "frontend/ui-components/progress-loader";
import { CanvasTimer } from "frontend/hooks/use-timer";
import TimerInput from "./timer-input";
import TimerToggle, { Sign } from "./timer-toggle";
import { useIsCIMReorderEnabled } from "utils/url-utils";

const playButton = (
  <svg
    className={style.playButton}
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M3 1V19L17.4 10L3 1Z" fill="currentColor" />
  </svg>
);

const pauseButton = (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect width="5" height="18" fill="currentColor" rx="1.5" x="4"></rect>
    <rect width="5" height="18" x="12" fill="currentColor" rx="1.5"></rect>
  </svg>
);

const stopButton = (
  <svg
    className={style.stopButton}
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M2 2H18V18H2V2Z" fill="currentColor" radius={10} />
  </svg>
);

const roundedStopButton = <div className={style.roundedStopButton}></div>;

const TimerController = ({
  timer,
  showTimer,
  setShowTimer,
  isReadOnly,
}: {
  timer: CanvasTimer;
  showTimer: boolean;
  setShowTimer: (show: boolean) => void;
  isReadOnly: boolean;
}) => {
  const [onEditEnded, setOnEditEnded] = useState<boolean>(false);

  const shouldDisplayTimesUp = showTimer && timer.config.over !== 0;
  const shouldDisplayPlayButton = !isReadOnly && (!timer.config?.isRunning || timer.config?.pause !== 0);
  const shouldDisplayPause = !isReadOnly && timer.config?.isRunning;
  const shouldDisplayStop = !isReadOnly && (timer.config?.isRunning || timer.config?.pause !== 0 || onEditEnded);
  const isCimReorderEnabled = useIsCIMReorderEnabled();

  const timerRef = useRef<any>(null);

  return (
    <>
      <div className={classNames(style.loader, "light", { [style.cimPosition]: isCimReorderEnabled })} ref={timerRef}>
        {shouldDisplayTimesUp ? (
          <div className={style.container}>
            <div className={style.countdownOver}>Time’s up! </div>
          </div>
        ) : (
          <div className={style.container}>
            <div className={style.shadow}></div>
            <div className={style.timerloader}>
              <ProgressLoader
                size={200}
                percent={timer.progress}
                strokeColor="#EBEDF3"
                loaderStrokeColor="#1973FF"
                fill={"white"}
                type={isCimReorderEnabled ? "reorder" : "empty-out"}
              />
            </div>
            <div className={style.timer}>
              <TimerInput timer={timer} setOnEditEnded={setOnEditEnded} isReadOnly={isReadOnly} />
              <div className={style.timerControllers}>
                {shouldDisplayStop && (
                  <button className={style.timerControlButton} onClick={timer.reset} data-testid="timer-stop-button">
                    {isCimReorderEnabled ? roundedStopButton : stopButton}
                  </button>
                )}
                {shouldDisplayPlayButton && (
                  <button className={style.timerControlButton} onClick={timer.start} data-testid="timer-play-button">
                    {playButton}
                  </button>
                )}
                {shouldDisplayPause && (
                  <button className={style.timerControlButton} onClick={timer.pause} data-testid="timer-pause-button">
                    {pauseButton}
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
      {showTimer && (
        <TimerToggle
          sign={timer.config.isRunning ? Sign.Minimize : Sign.Close}
          resetTimer={timer.reset}
          setShowTimer={setShowTimer}
        />
      )}
    </>
  );
};

export default TimerController;
